import dayjs from "dayjs";
import { Icon } from "@sellar-io/components";
import { OrderStage } from "../../helpers";
import { Link } from "react-router";
import {
  BUSINESS_FINANCE_CREDIT,
  BUSINESS_FINANCE_CREDIT_PAYOUT,
} from "../../../../constants/routes";

const preDispatchOrderStages: OrderStage[] = [
  "Accepted",
  "Requested",
  "Confirmed",
];

const preConfirmedOrderStages: OrderStage[] = ["Accepted", "Requested"];

type SellarCreditOrderSummaryStatusPaymentMessageProps = {
  orderStage: OrderStage;
  isDirector: boolean;
  invoicePaidAt: Date | null;
  nbCreditNotes: number;
  userType: "supplier" | "buyer";
  deferredPaymentId: string | null;
  invoiceId?: number;
};

export const SellarCreditOrderSummaryStatusPaymentMessage = ({
  userType,
  ...props
}: SellarCreditOrderSummaryStatusPaymentMessageProps) => {
  if (userType === "buyer") {
    return <SellarCreditOrderSummaryStatusPaymentMessageBuyer {...props} />;
  }

  if (userType === "supplier") {
    return <SellarCreditOrderSummaryStatusPaymentMessageSupplier {...props} />;
  }

  return null;
};

const SellarCreditOrderSummaryStatusPaymentMessageBuyer = ({
  isDirector,
  orderStage,
  nbCreditNotes,
  deferredPaymentId,
}: Omit<SellarCreditOrderSummaryStatusPaymentMessageProps, "userType">) => {
  if (nbCreditNotes > 0) {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="CHECKBOX_MARKED_CIRCLE" className="text-emerald-600" />
        <small className="text-zinc-500">
          Credit notes raised {isDirector ? `(${nbCreditNotes})` : ""}
        </small>
      </div>
    );
  }

  if (preConfirmedOrderStages.includes(orderStage) && !deferredPaymentId) {
    return null; // Hide text when the deferred payment does not exist
  }

  const isPreDispatchOrderStage = preDispatchOrderStages.includes(orderStage);

  if (isPreDispatchOrderStage) {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="INFORMATION" className="text-zinc-500" />
        <small className="text-zinc-500">
          Your supplier will now process your order
        </small>
      </div>
    );
  }

  if (orderStage === "Dispatched") {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="CHECK_CIRCLE" className="text-emerald-600" />
        <small className="text-zinc-500">
          Suppliers are paid upfront on dispatch
        </small>
      </div>
    );
  }
};

const SellarCreditOrderSummaryStatusPaymentMessageSupplier = ({
  orderStage,
  invoicePaidAt,
  nbCreditNotes,
  invoiceId,
}: Omit<SellarCreditOrderSummaryStatusPaymentMessageProps, "userType">) => {
  if (nbCreditNotes > 0) {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="CHECKBOX_MARKED_CIRCLE" className="text-emerald-600" />
        <small className="text-zinc-500">Credit notes raised</small>
      </div>
    );
  }

  if (invoicePaidAt) {
    return invoiceId ? (
      <Link to={`${BUSINESS_FINANCE_CREDIT_PAYOUT}?invoiceId=${invoiceId}`}>
        <div className="group flex flex-nowrap items-center">
          <Icon icon="INFORMATION" className="text-zinc-500" />
          <small className="ml-2 text-nowrap text-zinc-500 group-hover:underline">
            View your payout
          </small>
          <Icon icon="CHEVRON_RIGHT" size={0.7} />
        </div>
      </Link>
    ) : (
      <div className="flex items-center gap-2">
        <Icon icon="INFORMATION" className="text-zinc-500" />

        <small className="text-zinc-500">
          View your payout using the tag above
        </small>
      </div>
    );
  }

  if (orderStage === "Accepted" || orderStage === "Confirmed") {
    return (
      <div className="flex items-center gap-2">
        <Icon icon="INFORMATION" className="text-zinc-500" />
        <small className="text-zinc-500">
          Funds added to your next payout on dispatch
        </small>
      </div>
    );
  }

  if (orderStage === "Dispatched") {
    const today = dayjs();
    const nextWednesday = today.day(today.day() < 3 ? 3 : 10);

    return (
      <>
        <Link to={`${BUSINESS_FINANCE_CREDIT}?payout=next`}>
          <div className="group flex flex-nowrap items-center gap-2">
            <Icon icon="CHECKBOX_MARKED_CIRCLE" className="text-emerald-600" />
            <small className="text-nowrap text-zinc-500 group-hover:underline">
              Funds added to your next payout{" "}
              {nextWednesday.format("DD/MM/YYYY")}
            </small>
            <Icon icon="CHEVRON_RIGHT" size={0.7} />
          </div>
        </Link>
      </>
    );
  }
  return null;
};
